import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Human-readable message from a thrown value. Supabase/Postgrest errors are
 * plain objects (not Error instances), so reading only Error.message loses the
 * real cause — check both before falling back.
 */
export function errorMessage(err: unknown, fallback = "Unknown error"): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "object" && err !== null && "message" in err) {
    const msg = (err as { message: unknown }).message;
    // a present-but-empty/undefined message is less useful than the fallback
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Quizrun helpers
// ---------------------------------------------------------------------------

/**
 * The quizrun's display name. A stored `title` (free text, present on any kind)
 * wins outright; otherwise fall back to the computed "#N + month" label. If
 * there's no number either, just "Quizrun" (never render a blank or "#null").
 */
export const formatQuizrunLabel = (
  number: number | null,
  language: string,
  dateStart?: string,
  opts?: { title?: string | null },
): string => {
  if (opts?.title) return opts.title;
  const month = dateStart
    ? new Date(dateStart).toLocaleDateString("en-US", { month: "long" })
    : undefined;
  const base =
    number == null
      ? "Quizrun"
      : language === "en"
        ? `Quizrun #${number}`
        : `Quizrun ${language.toUpperCase()} #${number}`;
  return month ? `${base} ${month}` : base;
};

// ---------------------------------------------------------------------------
// Time helpers (H:MM:SS strings)
// ---------------------------------------------------------------------------

/**
 * Parse "H:MM:SS" or "MM:SS" to total seconds. Returns Infinity for falsy input.
 * A leading "-" negates the whole value (scores can go sub-zero), so
 * "-0:19:57" is -1197 rather than the sign being swallowed by the first part.
 */
export const parseTimeToSeconds = (time: string | null | undefined): number => {
  if (!time) return Infinity;
  const negative = time.startsWith("-");
  const bare = negative ? time.slice(1) : time;
  const seconds = bare.split(":").reduce((acc, t) => 60 * acc + +t, 0);
  return negative ? -seconds : seconds;
};

/** Format total seconds as "H:MM:SS" (supports negative values). */
export const formatSeconds = (seconds: number): string => {
  const abs = Math.abs(seconds);
  const sign = seconds < 0 ? "-" : "";
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  return `${sign}${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

/** Add two time strings. Returns null if either is missing. */
export const addTimes = (a: string | null, b: string | null): string | null => {
  if (!a || !b) return null;
  return formatSeconds(parseTimeToSeconds(a) + parseTimeToSeconds(b));
};

/**
 * Converts PostgreSQL interval string to HH:MM:SS format.
 * Returns null if interval is missing/invalid.
 */
export const intervalToString = (
  interval: string | null | undefined,
): string | null => {
  if (!interval || typeof interval !== "string") return null;
  const match = interval.match(/(\d+):(\d+):(\d+)/);
  if (match) {
    const [, hours, minutes, seconds] = match;
    return `${hours}:${minutes.padStart(2, "0")}:${seconds.padStart(2, "0")}`;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** Format city + country for display. */
export const formatLocation = (city: string, country: string): string =>
  country ? `${city}, ${country}` : city;

/** Computes the "Quizrun #N Month Year" display name (language tag for non-English
 * editions; "Quizrun Month Year" when number is null). App-side mirror of the DB
 * `quizrun_default_title` trigger, used ONLY to seed a title before a row exists
 * (the create dialog). Rendering an existing quizrun should read its stored,
 * NOT-NULL `title` directly instead of recomputing here. */
export const formatEventName = (
  number: number | null,
  language: string,
  dateStart: string,
): string => {
  const monthYear = new Date(dateStart).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const base =
    number == null
      ? "Quizrun"
      : language === "en"
        ? `Quizrun #${number}`
        : `Quizrun ${language.toUpperCase()} #${number}`;
  return `${base} ${monthYear}`;
};

/** Format date as "DD Month YYYY" (e.g. "14 February 2026"). */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

/**
 * Returns the URL only if it uses a safe http/https scheme, else undefined.
 * Use for DB-sourced values rendered into <a href> to block javascript: links.
 */
export const safeHref = (url: string | null | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    const scheme = new URL(url, window.location.origin).protocol;
    return scheme === "http:" || scheme === "https:" ? url : undefined;
  } catch {
    return undefined;
  }
};
